import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next'; // Import useTranslation hook
import { ApiBaseUrlContext } from '../App';
import TechnicianDropdown from './TechnicianDropdown';
import { formatDateByLocale } from '../utils/dateUtils';
import './TimeReports.css';
import '../boxitem.css';
import '../Button.css';
import '../forms.css';
import '../filter.css';
import '../topmenu.css';
import { ReactComponent as FilterIcon } from '../assets/svg/icon-filter.svg';
import { ReactComponent as SectionToggleArrow } from '../assets/svg/icon-toggle-section-arrow.svg';
import BoxItem from './BoxItem';
import * as XLSX from "xlsx";
import { useParams } from 'react-router-dom';
import '../table-list-2.css';
import GroupedDataTable from './GroupDataTable2'; 

const TimeReports = () => {
  const { t, i18n } = useTranslation(); // Using translation hook
  const apiBaseUrl = useContext(ApiBaseUrlContext);
  const [serviceReports, setServiceReports] = useState([]);
  const [filter, setFilter] = useState({
    dncmaxgroupname: '', // Group name filter
    machine: '',
    searchText: '',
    technician: '',
    startDate: '',
    endDate: '',
  });
  const [groups, setGroups] = useState([]); // Store group names
  const [machines, setMachines] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.body.classList.add('time-reports-page');
    return () => {
      document.body.classList.remove('time-reports-page');
    };
  }, []);

  useEffect(() => {
    const fetchClosedServiceReports = async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/mnt/api/service-reports/closed?includeDetails=true`);
        setServiceReports(response.data);
      } catch (error) {
        console.error(t('timeReports.errorFetchingClosedReports'), error);
      }
    };

    const fetchGroupsAndMachines = async () => {
      try {
        const [groupsResponse, machinesResponse] = await Promise.all([
          axios.get(`${apiBaseUrl}/mnt/api/machines/groups`),
          axios.get(`${apiBaseUrl}/mnt/api/machines/all`),
        ]);
        setGroups(groupsResponse.data); // Store groups in state
        console.log(groups);
        setMachines(machinesResponse.data);
      } catch (error) {
        console.error(t('timeReports.errorFetchingGroupsMachines'), error);
      }
    };
    console.log('Fetching closed service reports...');

    fetchClosedServiceReports();
    fetchGroupsAndMachines();
  }, [apiBaseUrl]);

  const handleFilterChange = (e) => {
    setFilter({ ...filter, [e.target.name]: e.target.value });
  };

  const handleTechnicianSelect = (selectedTechnician) => {
    setFilter(prevFilter => ({
      ...prevFilter,
      technician: selectedTechnician.name,
    }));
  };

  // Ensure group name filter is applied
  const { machineid } = useParams(); // Get machineid from URL

const filteredServiceReports = serviceReports.filter(({ report, technicians, materials }) => {
    const matchesGroup = !filter.dncmaxgroupname || report.dncmaxgroupname === filter.dncmaxgroupname;
    const matchesMachine = !filter.machine || report.machine === filter.machine;
    const matchesTechnician = !filter.technician || technicians.some(tech => tech.technician === filter.technician);
    const matchesSearchText = !filter.searchText || 
      [
        report.machine,
        report.fault,
        report.description_action,
        ...technicians.map(tech => tech.technician),
        ...materials.map(mat => mat.description),
      ].some(field => field?.toLowerCase().includes(filter.searchText.toLowerCase()));

    const matchesDateRange = (!filter.startDate || new Date(report.created_at) >= new Date(filter.startDate)) &&
                             (!filter.endDate || new Date(report.created_at) <= new Date(filter.endDate));

    const matchesMachineId = !machineid || report.machineid === parseInt(machineid); // NEW: Ensure machine ID matches

    return matchesMachineId && matchesGroup && matchesMachine && matchesTechnician && matchesSearchText && matchesDateRange;
});

  const formatEpochTime = (epochTime) => {
    if (epochTime > 9999999999) {
      return new Date(epochTime).toLocaleString();
    }
    return new Date(epochTime * 1000).toLocaleString();
  };

  const formatDate = (epochTime) => {
    const date = new Date(epochTime);
    return date.toLocaleDateString();
  };

  const formatTime = (epochTime) => {
    const date = new Date(epochTime);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const calculateDuration = (start, end) => {
    const startTime = new Date(start);
    const endTime = new Date(end);
    const totalMinutes = Math.floor((endTime - startTime) / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return { hours, minutes, durationText: `${hours}h ${minutes}m` };
  };

  const calculateCost = (hourlyRate, start, end) => {
    const { hours, minutes } = calculateDuration(start, end);
    const totalHours = hours + minutes / 60;
    return hourlyRate * totalHours;
  };

  const handleDownloadCSV = () => {
    const formatChoice = window.prompt(
      t('timeReports.csvPrompt'),
      "1"
    );

    const useEuropeanFormat = formatChoice === "1";
    const csvRows = [];

    const wrapInQuotes = (value) => {
      if (value === null || value === undefined) {
        return '""';
      }
      return `"${String(value).replace(/"/g, '""')}"`; // Escape any existing quotes in the value
    };

    const formatNumber = (number) => {
      return useEuropeanFormat
        ? number.toFixed(2).replace('.', ',')
        : number.toFixed(2);
    };

    csvRows.push([
      t('timeReports.csvHeaders.reportId'), 
      t('timeReports.csvHeaders.createdAt'), 
      t('timeReports.csvHeaders.updatedAt'), 
      t('timeReports.csvHeaders.status'), 
      t('timeReports.csvHeaders.technicians'), 
      t('timeReports.csvHeaders.machine'), 
      t('timeReports.csvHeaders.fault'), 
      t('timeReports.csvHeaders.description'),
      t('timeReports.csvHeaders.technician'), 
      t('timeReports.csvHeaders.date'), 
      t('timeReports.csvHeaders.timeDescription'), 
      t('timeReports.csvHeaders.startTime'), 
      t('timeReports.csvHeaders.endTime'), 
      t('timeReports.csvHeaders.duration'), 
      t('timeReports.csvHeaders.timeCost'),
      t('timeReports.csvHeaders.materialDescription'), 
      t('timeReports.csvHeaders.quantity'), 
      t('timeReports.csvHeaders.unit'), 
      t('timeReports.csvHeaders.materialPrice'), 
      t('timeReports.csvHeaders.materialSum'), 
      t('timeReports.csvHeaders.file')
    ].map(wrapInQuotes));

    filteredServiceReports.forEach(({ report, technicians, timeEntries = [], materials = [], files = [] }) => {
      const technicianNames = technicians.map(tech => tech.technician).join(', ');

      const maxRows = Math.max(timeEntries.length, materials.length, files.length);

      for (let i = 0; i < maxRows; i++) {
        const entry = timeEntries[i] || {};
        const material = materials[i] || {};
        const file = files[i] || {};
        const technicianName = technicians.find(tech => tech.techentryid === entry.techentryid)?.technician || '';

        const duration = entry.start_time && entry.end_time ? calculateDuration(entry.start_time, entry.end_time) : { durationText: '', hours: 0, minutes: 0 };
        const cost = entry.start_time && entry.end_time ? calculateCost(entry.hourly_rate, entry.start_time, entry.end_time) : 0;

        csvRows.push([
          i === 0 ? wrapInQuotes(report.servicereportid) : '',
          i === 0 ? wrapInQuotes(formatEpochTime(report.created_at)) : '',
          i === 0 ? wrapInQuotes(formatEpochTime(report.updated_at)) : '',
          i === 0 ? wrapInQuotes(report.status) : '',
          i === 0 ? wrapInQuotes(technicianNames) : '',
          i === 0 ? wrapInQuotes(report.machine) : '',
          i === 0 ? wrapInQuotes(report.fault) : '',
          i === 0 ? wrapInQuotes(report.description_action) : '',
          wrapInQuotes(technicianName),
          wrapInQuotes(entry.start_time ? formatDate(entry.start_time) : ''),
          wrapInQuotes(entry.timedescription || ''),
          wrapInQuotes(entry.start_time ? formatTime(entry.start_time) : ''),
          wrapInQuotes(entry.end_time ? formatTime(entry.end_time) : ''),
          wrapInQuotes(duration.durationText),
          wrapInQuotes(`${formatNumber(cost)}`),
          wrapInQuotes(material.description || ''),
          wrapInQuotes(material.quantity || ''),
          wrapInQuotes(material.unit || ''),
          wrapInQuotes(material.price ? `${formatNumber(material.price)}` : ''),
          wrapInQuotes(material.price ? `${formatNumber(material.price * material.quantity)}` : ''),
          wrapInQuotes(file.filename || '')
        ]);
      }

      csvRows.push([]);
    });

    const csvContent = csvRows.map(e => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "structured_service_reports.csv");
    link.click();
  };

  const handleDownloadXLSX = () => {
    const wsData = [
      [
        t("timeReports.csvHeaders.reportId"),
        t("timeReports.csvHeaders.createdAt"),
        t("timeReports.csvHeaders.updatedAt"),
        t("timeReports.csvHeaders.status"),
        t("timeReports.csvHeaders.technicians"),
        t("timeReports.csvHeaders.machine"),
        t("timeReports.csvHeaders.fault"),
        t("timeReports.csvHeaders.description"),
        t("timeReports.csvHeaders.technician"),
        t("timeReports.csvHeaders.date"),
        t("timeReports.csvHeaders.timeDescription"),
        t("timeReports.csvHeaders.startTime"),
        t("timeReports.csvHeaders.endTime"),
        t("timeReports.csvHeaders.duration"),
        t("timeReports.csvHeaders.timeCost"),
        t("timeReports.csvHeaders.materialDescription"),
        t("timeReports.csvHeaders.quantity"),
        t("timeReports.csvHeaders.unit"),
        t("timeReports.csvHeaders.materialPrice"),
        t("timeReports.csvHeaders.materialSum"),
        t("timeReports.csvHeaders.file")
      ],
    ];
  
    filteredServiceReports.forEach(({ report, technicians, timeEntries = [], materials = [], files = [] }) => {
      const technicianNames = technicians.map((tech) => tech.technician).join(", ");
  
      const maxRows = Math.max(timeEntries.length, materials.length, files.length);
  
      for (let i = 0; i < maxRows; i++) {
        const entry = timeEntries[i] || {};
        const material = materials[i] || {};
        const file = files[i] || {};
        const technicianName = technicians.find((tech) => tech.techentryid === entry.techentryid)?.technician || "";
  
        const duration = entry.start_time && entry.end_time ? calculateDuration(entry.start_time, entry.end_time) : { durationText: "", hours: 0, minutes: 0 };
        const cost = entry.start_time && entry.end_time ? calculateCost(entry.hourly_rate, entry.start_time, entry.end_time) : 0;
  
        wsData.push([
          i === 0 ? report.servicereportid : "",
          i === 0 ? formatEpochTime(report.created_at) : "",
          i === 0 ? formatEpochTime(report.updated_at) : "",
          i === 0 ? report.status : "",
          i === 0 ? technicianNames : "",
          i === 0 ? report.machine : "",
          i === 0 ? report.fault : "",
          i === 0 ? report.description_action : "",
          technicianName,
          entry.start_time ? formatDate(entry.start_time) : "",
          entry.timedescription || "",
          entry.start_time ? formatTime(entry.start_time) : "",
          entry.end_time ? formatTime(entry.end_time) : "",
          duration.durationText,
          `${cost.toFixed(2)}`,
          material.description || "",
          material.quantity || "",
          material.unit || "",
          material.price ? `${material.price.toFixed(2)}` : "",
          material.price ? `${(material.price * material.quantity).toFixed(2)}` : "",
          file.filename || "",
        ]);
      }
  
      wsData.push([]);
    });
  
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Time Reports");
  
    XLSX.writeFile(wb, "time_reports.xlsx");
  };


  const handleOpenReport = (reportId) => {
    navigate(`/service-reports/${reportId}`);
  };

  const toggleFilters = () => {
    setShowFilters(prevShowFilters => !prevShowFilters);
  };
  const handleOpenFault = (report) => {
    console.log("👉 handleOpenFault called with", report);
    const fault = { 
      id: report.faultid,
      description: report.fault,
      machine: report.machine,
      machineid: report.machineid,
      status: "Closed",
      technician: report.technician || "",
    };
    navigate(`/service-reports`, {
      state: {
        mode: 'edit',
        fault,
        serviceReportId: report.servicereportid,
        navKey: Date.now(), // ensures the state always changes
      }
    });
  };
  

  
  const handleResetFilter = () => {
    // Reset to initial filter state
    setFilter({
      dncmaxgroupname: '',
      machine: '',
      searchText: '',
      technician: '',
      startDate: '',
      endDate: '',
    });
  };
  return (

    <>
  
     {showFilters && (
        
        <div className="filter-container" onClick={() => setShowFilters(!showFilters)}>
          <div className="filter" onClick={(e) => e.stopPropagation()}>
            <div className="filter__fields">
              <div className="filter__label">{t('timeReports.machine')}</div>
              <div className="entry-item">
                <select
                  id="machine"
                  name="machine"
                  className="form-select"
                  value={filter.machine}
                  onChange={handleFilterChange}
                >
                  <option value="">{t('timeReports.all')}</option>
                  {machines.map(machine => (
                    <option key={machine.machineid} value={machine.machine}>{machine.machine}</option>
                  ))}
                </select>
              </div>
  
              <div className="filter__label">{t('timeReports.technician')}</div>
              <div className="entry-item">
                <TechnicianDropdown
                  onSelectTechnician={handleTechnicianSelect}
                  value={filter.technician}
                />
              </div>
  
              <div className="filter__label">{t('timeReports.startDate')}</div>
              <div className="entry-item">
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  className="form-control"
                  value={filter.startDate}
                  onChange={handleFilterChange}
                />
              </div>
  
              <div className="filter__label">{t('timeReports.endDate')}</div>
              <div className="entry-item">
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  className="form-control"
                  value={filter.endDate}
                  onChange={handleFilterChange}
                />
              </div>
  
              <div className="filter__label">{t('timeReports.search')}</div>
              <div className="entry-item">
                <input
                  type="text"
                  id="searchText"
                  name="searchText"
                  className="form-control"
                  value={filter.searchText}
                  onChange={handleFilterChange}
                  placeholder={t('timeReports.searchPlaceholder')}
                />
              </div>
            
            </div>
  
            <div className="filter__buttons">
              <button 
                type="button"
                className="button--grey"
                id="reset-filter"
                onClick={handleResetFilter}
              >
                {t('timeReports.reset')}
              </button>
            </div>  
              
          </div>
        </div>
         )}
  
  
        <div className="content">
        <div className="top-menu">
          <div className="top-menu__divider"></div>
          <div className="top-menu__icon-button top-menu__filter-button" onClick={toggleFilters}>
            <FilterIcon />
          </div>

          <div className="button-container"> {/* ✅ Add a container for better styling */}
            <button className="btn-primary" onClick={handleDownloadCSV}>
              {t("timeReports.downloadCSV")}
            </button>

            <div className="button-space"></div> {/* ✅ This adds space between the buttons */}

            <button className="btn-primary" onClick={handleDownloadXLSX}>
              {t("timeReports.downloadXLSX")}
            </button>
          </div>
        </div>

  

<GroupedDataTable
  groupedData={{
    [t('timeReports.closedServiceReports')]: filteredServiceReports,
  }}
  isFiltered = {Object.values(filter).some(Boolean)}
  headers={[
    {
      label: t("detailedServiceReport.tableHeaders.description"),
      render: ({ report }) =>
        report.fault.length > 50
          ? `${report.fault.substring(0, 40)}...`
          : report.fault,
      elasticCell: true,
      colorStyle: "block",
      colorValueFrom: () => "grey",
    },
    {
      label: t("timeReports.machine"),
      render: ({ report }) => report.machine,
      hideMobile: true,
    },    
    {
      label: t("detailedServiceReport.tableHeaders.date"),
      render: ({ report }) =>
        formatDateByLocale(Math.floor(report.updated_at / 1000), i18n.language),
      hideMobile: true,
    },
    {
      label: t("timeReports.technician"),
      render: ({ technicians }) =>
        technicians.map((t) => t.technician).join(", ") || t("timeReports.unknown"),
      hideMobile: true,
    },
    {
      label: t("timeReports.id"),
      render: ({ report }) => report.servicereportid,
      hideMobile: true,
    },

  ]}
  rowKeyFn={({ report }) => report.servicereportid}
  getPrimaryActionLabel={() => t("timeReports.serviceReport")}
  onPrimaryAction={({ report }) => handleOpenReport(report.servicereportid)}
  contextualOptionsFn={({ report }) => [
    {
      label: t("timeReports.edit"),
      onClick: (e) => {
        if (e?.preventDefault) e.preventDefault();
        handleOpenFault(report);
      },
    },
  ]}
  groupMeta={{
    [t('timeReports.closedServiceReports')]: {
      title: t("timeReports.closedServiceReports"),
      description: t('timeReports.TitleDescriptionClosedReports'),
      emptyMessage: t("timeReports.noClosedReports"),
    },
  }}
/>


  {/*

          {filteredServiceReports.length === 0 ? (
            <div className="content__machine-list-empty">{t('timeReports.noClosedReports')}</div>
          ) : (
            <>
            {filteredServiceReports.map(({ report, technicians }) => (
  
              <div key={report.servicereportid}>
                <BoxItem
                  color="grey"
                  title={report.machine}
                  infoVisible={{ 
                    status: ['grey', t('timeReports.closed')],  // Translated "Closed"
                    date: formatDateByLocale(Math.floor(report.updated_at / 1000), i18n.language),
                  }}
                  infoToggle={{ 
                    [t('timeReports.technician')]: technicians.map((tech) => tech.technician).join(', ') || t('timeReports.unknown'),  // Translated "Unknown"
                    [t('timeReports.id')]: report.servicereportid,  // Translated "ID"
                  }}
                  buttonAction={{ 
                    name: t('timeReports.serviceReport'),  // Translated "Service Report"
                    handler: () => handleOpenReport(report.servicereportid),
                  }}
                  options={{
                    [t('timeReports.edit')]: () => handleOpenFault(report)
                  }}
                  desc={report.fault.length > 50 ? `${report.fault.substring(0, 40)}...` : report.fault}
                />
              </div>
  
            ))}
            </>
          )}
  
*/}


      </div>
  
      </>
  
    );
  };
  
  export default TimeReports;
  